import clsx from "clsx";
import { motion } from "framer-motion";
import { graphql } from "gatsby";
import React, { FC, useMemo } from "react";

import { RichText } from "../../rich-text/rich-text";
import { VerticalImage, VerticalSliderContainer } from "../../sliders/vertical-slider";

export interface VerticalSliderSectionProps extends Queries.VerticalSliderSectionDataFragment {
  containerClassName?: string;
}

function transformSlides(slides?: VerticalSliderSectionProps["slidesLeft"]): VerticalImage[] {
  return (slides ?? []).map((slide) => ({
    img: slide?.media?.gatsbyImageData,
    backgroundColor: slide?.backgroundColor,
  }));
}

export const VerticalSliderSection: FC<VerticalSliderSectionProps> = ({
  slidesLeft,
  slidesRight,
  speed,
  description,
  title,
  containerClassName,
}) => {
  const imagesLeft = useMemo(() => transformSlides(slidesLeft), [slidesLeft]);
  const imagesRight = useMemo(() => transformSlides(slidesRight), [slidesRight]);
  return (
    <div
      className={clsx(
        "flex gap-8 2xl:gap-12 h-full bg-primary px-8 lg:px-20",
        "h-[100vh] min-h-[50vh] relative z-10 section-overlap-top",
        containerClassName
      )}
    >
      {/* LEFT side */}
      <motion.div
        initial={{ y: "100vh" }}
        animate={{ y: 0 }}
        transition={{ ease: "easeInOut", duration: 0.5 }}
        className="flex-1 my-auto text-light text-xl pr-2"
      >
        <h2 className="text-6xl leading-tight font-medium pb-8">{title}</h2>
        {description?.raw && <RichText raw={description.raw} />}
      </motion.div>
      {/* RIGHT side */}
      <VerticalSliderContainer
        baseVelocity={speed ?? undefined}
        containerClassName="gap-4 justify-center hidden xl:flex"
        itemsLists={[imagesLeft, imagesRight]}
      />
    </div>
  );
};

export const query = graphql`
  fragment VerticalSlideData on ContentfulVerticalSlide {
    media {
      gatsbyImageData
      url
    }
    backgroundColor
    id
  }
  fragment VerticalSliderSectionData on ContentfulVerticalSliderSection {
    title
    description {
      raw
    }
    speed
    slidesLeft {
      ...VerticalSlideData
    }
    slidesRight {
      ...VerticalSlideData
    }
  }
`;
